
import {
    getManageOwnedAssetsPostLoginRoute,
    isDashboardRoute,
    shouldRestrictCoOwnerMenuSync
} from '@/helpers/coOwnerSidebarMenu';
import {
    isManageAssetsListRoute,
    resolveManageAssetsNavigationPath,
    isManageAssetsNavigationInFlight,
    beginManageAssetsNavigationPending,
    endManageAssetsNavigationPending
} from '@/helpers/manageAssetsNavigation';
import { isSessionValid } from '@/helpers/authSession';

function normalizeRoutePath(path) {
    if (path == null || path === '') {
        return '';
    }
    const raw = String(path).trim().toLowerCase();
    if (!raw) {
        return '';
    }
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
        return raw;
    }
    return raw.startsWith('/') ? raw : `/${raw}`;
}

function moduleRouteMatches(module, targetPath) {
    if (!module || !targetPath) {
        return false;
    }
    const route = normalizeRoutePath(module.moduleRoute);
    const target = normalizeRoutePath(targetPath);
    if (!route || !target) {
        return false;
    }
    return route === target || target.startsWith(`${route}/`);
}

/**
 * Search module permissions including nested subModules and categoryModules.
 */
function hasModuleAccess(modules, moduleCode, targetPath) {
    if (!Array.isArray(modules) || modules.length === 0) {
        return false;
    }

    for (let i = 0; i < modules.length; i++) {
        const mod = modules[i];
        if (mod == null) {
            continue;
        }

        if (moduleCode != null && moduleCode !== '' && mod.moduleCode == moduleCode) {
            return true;
        }

        if (targetPath && moduleRouteMatches(mod, targetPath)) {
            return true;
        }

        if (mod.subModules && hasModuleAccess(mod.subModules, moduleCode, targetPath)) {
            return true;
        }

        if (mod.categoryModules && hasModuleAccess(mod.categoryModules, moduleCode, targetPath)) {
            return true;
        }
    }

    return false;
}

function getFallbackRoute() {
    return shouldRestrictCoOwnerMenuSync()
        ? getManageOwnedAssetsPostLoginRoute()
        : '/dashboard';
}

function guardMyroute(to, from, next) {
    if (isDashboardRoute(to) && shouldRestrictCoOwnerMenuSync()) {
        next(getManageOwnedAssetsPostLoginRoute());
        return;
    }

    var isAuthenticated = false;
    // Reuse centralized session validation (token present + JWT not expired).
    if (isSessionValid() && localStorage.getItem('moduleList') != null && localStorage.getItem('moduleList') != "") {
        isAuthenticated = true;
    }
    else {
        isAuthenticated = false;
    }
    if (isAuthenticated) {
        let moduleList = JSON.parse(localStorage.getItem('moduleList'));
        const moduleCode = to.meta && to.meta.moduleCode;
        let allowedByModule = hasModuleAccess(moduleList, moduleCode, to.path);

        const pathLower = normalizeRoutePath(to.path);
        if (!allowedByModule && (pathLower.startsWith('/ticket') || pathLower.startsWith('/viewticket') || pathLower.startsWith('/editticket'))) {
            allowedByModule =
                hasModuleAccess(moduleList, 7011, to.path) ||
                hasModuleAccess(moduleList, 7010, to.path);
        }

        if (!allowedByModule && pathLower.startsWith('/manageassets/sharehistory')) {
            allowedByModule =
                hasModuleAccess(moduleList, 1004, to.path) ||
                hasModuleAccess(moduleList, 2006, to.path);
        }

        const allowDashboard =
            to.name == 'Dashboard' && !shouldRestrictCoOwnerMenuSync();

        if (allowedByModule || allowDashboard || to.name == 'My Profile' || to.name == 'Manage Assets | Asset Detial View ') {
            next();
        } else {
            next(getFallbackRoute());
        }
    } else {
        // Prefer Vue Router navigation (global guard also covers this after refresh).
        next('/login');
    }
}

async function guardManageAssetsListRoute(to, from, next) {
    if (!isManageAssetsListRoute(to.path)) {
        next();
        return;
    }

    if (isManageAssetsNavigationInFlight()) {
        next();
        return;
    }

    beginManageAssetsNavigationPending();
    try {
        const targetPath = await resolveManageAssetsNavigationPath(true);
        if (targetPath && targetPath !== normalizeRoutePath(to.path)) {
            next(targetPath);
            return;
        }
    } catch (error) {
        console.warn('Manage assets navigation redirect failed', error);
    } finally {
        endManageAssetsNavigationPending();
    }

    next();
}

function LoginGuard(to, from, next) {
    // Reuse the same session validity check used by protected navigation.
    if (!isSessionValid()) {
        next();
        return;
    }

    import('@/helpers/coOwnerSidebarMenu')
        .then(async ({ resolvePostLoginRedirectPath }) => {
            const path = await resolvePostLoginRedirectPath('/dashboard');
            next(path);
        })
        .catch(() => {
            next('/dashboard');
        });
}

export default {
    LoginGuard,
    guardMyroute,
    guardManageAssetsListRoute,
    hasModuleAccess,
    normalizeRoutePath
};
